import { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

import { useAppDispatch } from '@store/hooks/useAppDispatch';
import { fetchMachineOperations, selectPlanboardSearchValue } from '@reducers/v2/planboard';
import { MACHINES_PER_REQUEST } from '@v2/pages/planboard/constants';

const getPageLimit = (page: number, initialLimit: number) => {
  const basePower = 1.5;
  const limit = page > 0 ? Math.floor(initialLimit * Math.pow(basePower, page)) : initialLimit;

  let skip = 0;
  for (let i = 0; i < page; i++) {
    skip += i > 0 ? Math.floor(initialLimit * Math.pow(basePower, i)) : initialLimit;
  }

  return { limit, skip };
};

const useColumnFetch = (machineId: number, { top = MACHINES_PER_REQUEST, total, filter }: { top?: number; total?: number; filter?: string } = {}) => {
  const dispatch = useAppDispatch();
  const observerRef = useRef<IntersectionObserver>(null);
  const observedEntryRef = useRef(null);
  const planboardSearchValue = useSelector(selectPlanboardSearchValue);

  const [loading, setLoading] = useState(true);

  const pageRef = useRef(0);

  const fetchColumnMachines = useCallback(
    async (isDelayed = false) => {
      setLoading(true);
      const { limit, skip } = getPageLimit(pageRef.current, top);

      await dispatch(fetchMachineOperations({ machineId, top: limit, skip, filter }));
      if (isDelayed) {
        setTimeout(() => setLoading(false), 150);
      } else {
        setLoading(false);
      }
      pageRef.current += 1;
      setLoading(false);
    },
    [machineId, top, filter]
  );

  useEffect(() => {
    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, []);

  useEffect(() => {
    setLoading(true);
    pageRef.current = 1;

    (async () => {
      await dispatch(fetchMachineOperations({ machineId, top, filter, search: planboardSearchValue }));
      setTimeout(() => setLoading(false), 150);
    })();
  }, [filter, machineId, top, dispatch, planboardSearchValue]);

  useEffect(() => {
    if (!observedEntryRef.current) {
      return;
    }

    const observer = new IntersectionObserver(
      async (entries) => {
        const shouldRequest = entries[0].isIntersecting && !loading;
        const isLimit = top * pageRef.current >= (total ?? 0);

        if (shouldRequest && !isLimit) {
          fetchColumnMachines(true);
        }
      },
      { threshold: 1 }
    );

    observer.observe(observedEntryRef.current);

    return () => {
      if (observedEntryRef.current) {
        observer.unobserve(observedEntryRef.current);
      }
    };
  }, [observedEntryRef, machineId, total, loading, fetchColumnMachines, top]);

  return { fetchColumnMachines, observedEntryRef };
};

export default useColumnFetch;
